import { useEffect, useMemo, useRef } from "react";
import { ChatMessage, ChatMessageArtifact } from "@store/messages.store";
import { useChatWidgetLogic } from "@shared/hooks/useChatWidgetLogic";
import { useBreakpoint } from "@avenue-8/sidekick-ui";

const WIDGET_ARTIFACT_KIND_PRIORITY: ChatMessageArtifact["kind"][] = [
  "widget-cma-details",
  "widget-listings-search",
  "widget-listing-details",
];

type Props = {
  messages: ChatMessage[];
  currentThread: string | undefined;
};

export const useOpenLastChatWidget = (props: Props) => {
  const { messages, currentThread } = props;
  const {
    setArtifactAsChatWidget,
    resetChatWidget,
    toggleChatWidgetDrawer,
    chatWidget,
    temporaryWidgetThreadId,
  } = useChatWidgetLogic();
  const isLaptopDown = useBreakpoint("laptop");
  const engagedArtifactIdRef = useRef<string | null>(null);

  const { lastThreadWidgetArtifact, isArtifactOnLastMessage } = useMemo(() => {
    if (messages.length === 0) {
      return { lastThreadWidgetArtifact: null, isArtifactOnLastMessage: false };
    }

    const lastMessage = messages[messages.length - 1];
    const widgetArtifacts = messages
      .flatMap((message) => message.artifacts || [])
      .filter((artifact) => artifact.kind.startsWith("widget-"));

    const lastArtifact =
      WIDGET_ARTIFACT_KIND_PRIORITY.map((kind) =>
        widgetArtifacts.filter((artifact) => artifact.kind === kind).at(-1),
      ).find((artifact) => artifact) ?? null;

    const isOnLastMessage =
      lastArtifact &&
      lastMessage?.artifacts?.some(
        (artifact) => artifact.id === lastArtifact.id,
      );

    return {
      lastThreadWidgetArtifact: lastArtifact,
      isArtifactOnLastMessage: !!isOnLastMessage,
    };
  }, [messages]);

  useEffect(() => {
    if (!lastThreadWidgetArtifact) {
      engagedArtifactIdRef.current = null;
      return;
    }

    if (isLaptopDown) return;

    if (engagedArtifactIdRef.current === lastThreadWidgetArtifact.id) return;
    engagedArtifactIdRef.current = lastThreadWidgetArtifact.id;

    setArtifactAsChatWidget(lastThreadWidgetArtifact);

    if (isArtifactOnLastMessage) {
      toggleChatWidgetDrawer("expanded");
    }
  }, [
    lastThreadWidgetArtifact,
    isLaptopDown,
    setArtifactAsChatWidget,
    toggleChatWidgetDrawer,
    isArtifactOnLastMessage,
  ]);

  useEffect(() => {
    if (lastThreadWidgetArtifact) return;

    const preservesOpenCanvasWidget =
      chatWidget?.temporary &&
      (!currentThread || temporaryWidgetThreadId === currentThread);
    if (chatWidget && !preservesOpenCanvasWidget) {
      resetChatWidget();
    }
  }, [
    lastThreadWidgetArtifact,
    chatWidget,
    temporaryWidgetThreadId,
    currentThread,
    resetChatWidget,
  ]);

  return { lastThreadWidgetArtifact };
};
